import { z } from 'zod';
import { DISPLAYED_REQUEST_STATUSES, impactSchema, intakeChannelSchema, requestTypeSchema } from './domain.js';
const optionalText = z
    .string()
    .trim()
    .max(2000)
    .optional()
    .transform((v) => (v === undefined || v === '' ? undefined : v));
const optionalUrl = optionalText.refine((v) => v === undefined || z.url().safeParse(v).success, {
    error: 'Adresse invalide.',
});
/**
 * Body of POST /api/requests. Strict: unknown keys are refused. There is no
 * priority, order or retained impact here — they belong to the PO
 * (see `PO_ONLY_REQUEST_FIELDS`, refused explicitly by the API).
 * `productId: null` is the « Autre / je ne sais pas » option (ADR-003).
 */
export const requestSubmissionBaseSchema = z
    .object({
    type: requestTypeSchema,
    productId: z.uuid().nullable(),
    title: z.string().trim().min(3, 'Le titre est trop court.').max(200),
    description: z.string().trim().min(1, 'La description est obligatoire.').max(20000),
    impactDeclared: impactSchema,
    url: optionalUrl,
    cmsArticleUrl: optionalUrl,
    clientEmail: optionalText.refine((v) => v === undefined || z.email().safeParse(v).success, {
        error: 'Adresse email invalide.',
    }),
    /** Free text with no fixed form: only its presence is checked, never its format. */
    clientNcct: optionalText,
    /**
     * « Équipe » (ADR-005 §6): only sent when several teams of the requester
     * may submit on the product (or, without product, when it has several teams).
     */
    teamId: z.uuid().optional(),
    /**
     * Requester other than the signed-in user (ADR-004, screen 15). Accepted
     * from a PO only: any other role gets 403. Absent = the signed-in user.
     */
    requesterId: z.uuid().optional(),
    /** How the request arrived (ADR-004). PO only when it is not `portal`. */
    intakeChannel: intakeChannelSchema.optional(),
    /** Duplicate suggestions shown before sending (ADR-009), journaled by the API with their outcome. */
    duplicateSuggestions: z.lazy(() => duplicateSuggestionsSchema).optional(),
})
    .strict();
/**
 * Adds the per-product rules on top of the base schema.
 * - `settings === null`: no product (« Autre / je ne sais pas ») → no conditional field required.
 * - requiresCmsArticleUrl → CMS article URL required.
 * - requiresClientInfo → client email and NCCT required.
 * - `eligibleTeams` (when given): with several, « Équipe » is required and must be one of them;
 *   with one, the team is deduced and the field is not shown.
 * Fields the product does not enable are dropped, so a stale value typed
 * before switching product is never stored.
 */
export function requestSubmissionSchema(settings, eligibleTeams) {
    return requestSubmissionBaseSchema
        .superRefine((value, ctx) => {
        if (eligibleTeams && eligibleTeams.length > 1) {
            if (!value.teamId) {
                ctx.addIssue({ code: 'custom', path: ['teamId'], message: 'Choisissez l’équipe pour laquelle la demande est faite.' });
            }
            else if (!eligibleTeams.some((t) => t.id === value.teamId)) {
                ctx.addIssue({ code: 'custom', path: ['teamId'], message: 'Cette équipe n’a pas accès à ce produit.' });
            }
        }
        if (!settings)
            return;
        if (settings.requiresCmsArticleUrl && !value.cmsArticleUrl) {
            ctx.addIssue({ code: 'custom', path: ['cmsArticleUrl'], message: "L'URL de l'article est obligatoire." });
        }
        if (settings.requiresClientInfo) {
            if (!value.clientEmail) {
                ctx.addIssue({ code: 'custom', path: ['clientEmail'], message: "L'email du client est obligatoire." });
            }
            if (!value.clientNcct) {
                ctx.addIssue({ code: 'custom', path: ['clientNcct'], message: 'Le NCCT du client est obligatoire.' });
            }
        }
    })
        .transform((value) => ({
        ...value,
        cmsArticleUrl: settings?.requiresCmsArticleUrl ? value.cmsArticleUrl : undefined,
        // « URL concernée » is offered whenever the CMS article URL is not.
        url: settings?.requiresCmsArticleUrl ? undefined : value.url,
        clientEmail: settings?.requiresClientInfo ? value.clientEmail : undefined,
        clientNcct: settings?.requiresClientInfo ? value.clientNcct : undefined,
        // A single eligible team is deduced: the field is neither shown nor needed.
        teamId: eligibleTeams && eligibleTeams.length === 1 ? eligibleTeams[0].id : value.teamId,
    }));
}
/**
 * Fields only the PO writes (priority, order, retained impact, and the rest of
 * the qualification). Refused explicitly — not silently dropped — on every
 * endpoint a Demandeur or a Référent can call.
 */
export const PO_ONLY_REQUEST_FIELDS = [
    'priority',
    'impactRetained',
    'draftOrder',
    'publishedOrder',
    'estimate',
    'status',
    'targetVersionId',
    'plannedDeliveryDate',
    'ownerId',
    'duplicateOfId',
];
/** Body of POST /api/requests/:id/messages (the `body` field of the multipart form, or JSON). */
export const replyBodySchema = z
    .object({
    body: z.string().trim().min(1, 'Le message est vide.').max(20000),
})
    .strict();
/** Statuses a list may be filtered by. « En attente de réponse » is not one of them: it is an indicator. */
export const requestListStatusFilterSchema = z.enum(DISPLAYED_REQUEST_STATUSES);
export const REQUEST_LIST_SCOPES = ['mine', 'team'];
export const requestListQuerySchema = z
    .object({
    scope: z.enum(REQUEST_LIST_SCOPES).default('mine'),
    status: requestListStatusFilterSchema.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(50).default(6),
})
    .strict();
// ---------------------------------------------------------------------------
// Duplicate search (ADR-009): side panel of 02 and 15, screen 16, banner of 08
// ---------------------------------------------------------------------------
/**
 * What the form has typed so far. The search runs on title and description,
 * plus the exact signals (CMS article URL, client email, client NCCT).
 * `requesterId`: screen 15, the colleague the PO types for (isMine, isSubscribed).
 */
export const duplicateSearchSchema = z
    .object({
    type: requestTypeSchema,
    productId: z.uuid().nullable(),
    title: z.string().trim().max(200).default(''),
    description: z.string().trim().max(20000).default(''),
    cmsArticleUrl: optionalText,
    clientEmail: optionalText,
    clientNcct: optionalText,
    requesterId: z.uuid().optional(),
})
    .strict();
/** Suggestions shown before sending: those of the side panel, and the one of screen 16 if it opened. */
export const duplicateSuggestionsSchema = z
    .object({
    shown: z.array(z.uuid()).max(20).default([]),
    prompted: z.uuid().optional(),
})
    .strict();
/** « Revenir au formulaire » (screen 16) or « Annuler » with suggestions shown: journaled as `ignored`. */
export const duplicateIgnoredSchema = duplicateSearchSchema
    .extend({
    candidateIds: z.array(z.uuid()).min(1).max(20),
    place: z.enum(['panel', 'prompt']),
})
    .strict();
/** Counts of a list of items: « 2 évolutions, 5 corrections ». */
export function countReleaseNoteItems(items) {
    return {
        features: items.filter((i) => i.type === 'feature').length,
        fixes: items.filter((i) => i.type === 'fix').length,
    };
}
//# sourceMappingURL=requests.js.map